// @ts-check
/** @typedef {import('@datahooks/types/tagging.types').OrganisationTag} OrganisationTag */

import React, { useState } from "react";

import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from "@material-tailwind/react";
import { RiFilterLine } from "react-icons/ri";

import { TestableSelect, TestableSelectItem, TestableButton } from '@components/general/TestableComponents';

import PropTypes from "prop-types";
import { OrganisationTagPropType } from "@datahooks/types/tagging.types";

/**
 * @callback OnFiltered
 * @param {{tag: number, value: number}} filter
 * @returns {void} 
*/

/**
 * Popup to allow setting of filter tags/values
 * 
 * @param {Object} props
 * @param {OnFiltered} props.onFiltered Callback when a filter is added
 * @param {OrganisationTag[]} props.tags List of available tags to filter by
 * @param {JSX.Element} [props.buttonLabel] Optional label for the button that opens the filter dialog
 * @param {boolean} [props.triggerDisabled] Optional flag to disable the button that opens the filter dialog
 * @returns 
 */
const MenuLinkFilterPopup = ({ onFiltered, tags, buttonLabel, triggerDisabled = false }) => {
    const [ isFilterOpen, setIsFilterOpen ] = useState(false);
    const [ filterTag, setFilterTag ] = useState(/** @type {number | ''} */(''));
    const [ filterValue, setFilterValue ] = useState(/** @type {number | ''} */(''));

    const selectedTag = tags.find(tag => tag.tag_id === Number(filterTag));
    const tag_values = selectedTag?.tag_values || [];

    const handleClose = () => {
        setFilterTag('')
        setFilterValue('');
        setIsFilterOpen(false);
    }

    const handleAddFilter = () => {
        onFiltered?.({ tag: Number(filterTag), value: Number(filterValue) });

        setFilterTag('');
        setFilterValue('');
        setIsFilterOpen(false);
    }

    return (
        !isFilterOpen ? (
            <TestableButton disabled={triggerDisabled} dataTestId='menulinks-tag-filter-button' className='h-auto mt-2 py-0 px-3' variant='outlined' onClick={() => { setIsFilterOpen(true); }}>
                { buttonLabel ?? <RiFilterLine size={20}/> }
            </TestableButton>
        ) : (
            <Dialog open={isFilterOpen} handler={handleClose} size='sm'>
            <DialogHeader>Add Filter</DialogHeader>
            <DialogBody>
                <div className='flex flex-col gap-4'>
                <TestableSelect
                    dataTestId='menulinks-tag-filter-select'
                    label='Tag'
                    value={filterTag === '' ? '' : filterTag}
                    onChange={(v) => { setFilterTag(v === '' ? '' : Number(v)); setFilterValue(''); }}
                >
                    {tags.map((tag) => 
                        <TestableSelectItem dataTestId='menulinks-tag-filter-select-item' key={tag.tag_id} value={tag.tag_id}>{tag.tag_title}</TestableSelectItem>
                    )}
                </TestableSelect>

                { tag_values.length > 0 && (
                    <TestableSelect
                        dataTestId='menulinks-tag-value-filter-select'
                        label='Value'
                        value={filterValue === '' ? '' : filterValue}
                        disabled={!filterTag}
                        onChange={(tagValue) => setFilterValue(tagValue === '' ? '' : Number(tagValue))}
                    >
                        {(tag_values || []).map((tagValue) => (
                        <TestableSelectItem dataTestId='menulinks-tag-value-filter-select-item' key={tagValue.tag_value_id} value={tagValue.tag_value_id}>{tagValue.title}</TestableSelectItem>
                        ))}
                    </TestableSelect>
                )}
                </div>
            </DialogBody>
            <DialogFooter>
                <Button variant='text' color='blue-gray' onClick={() => setIsFilterOpen(false)}>Cancel</Button>
                <Button className='ml-2' color='blue' onClick={handleAddFilter} disabled={filterTag === '' || filterValue === ''}>Add</Button>
            </DialogFooter>
            </Dialog>
        )
    );
};

MenuLinkFilterPopup.propTypes = {
    onFiltered: PropTypes.func.isRequired,
    tags: PropTypes.arrayOf(OrganisationTagPropType).isRequired,
    buttonLabel: PropTypes.element,
    triggerDisabled: PropTypes.bool,
};

export default MenuLinkFilterPopup;